import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type SyntheticEvent,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { Loader } from "../Loader/Loader.tsx";

import styles from "./PdfViewer.module.scss";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

interface PdfViewerProps {
  /** URL of the PDF asset to render. */
  src: string;
  /** Optional accessible label for the document region. */
  ariaLabel?: string;
  /** Optional className applied to the outer container. */
  className?: string;
}

const preventEvent = (event: SyntheticEvent) => {
  event.preventDefault();
};

export const PdfViewer = ({
  src,
  ariaLabel = "Document viewer",
  className,
}: PdfViewerProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => {
      setPageWidth(element.clientWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const containerClassName = className
    ? `${styles.viewer} ${className}`
    : styles.viewer;

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      role="region"
      aria-label={ariaLabel}
      onContextMenu={preventEvent}
      onCopy={preventEvent}
      onCut={preventEvent}
      onDragStart={preventEvent}
    >
      <Document
        file={src}
        onLoadSuccess={({ numPages: loadedNumPages }) => {
          setNumPages(loadedNumPages);
        }}
        loading={<Loader text="Loading document..." />}
        error={
          <div className={styles.error} role="alert">
            Unable to load the document. Please try again later.
          </div>
        }
        noData={
          <div className={styles.error} role="alert">
            No document specified.
          </div>
        }
      >
        {Array.from({ length: numPages }, (_, index) => (
          <Page
            key={index}
            pageNumber={index + 1}
            width={pageWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className={styles.page}
            loading=""
          />
        ))}
      </Document>
    </div>
  );
};
